import { API_BASE_URL, apiRequest, type ApiRequestOptions } from '../http.ts';
import type { APIResponse } from './types/common.ts';
import type { OrderCancelData, OrdersResponse } from './types/trading.ts';

export interface ManualOrderContract {
  symbol: string;
  sec_type?: string | null;
  con_id?: number | null;
  exchange?: string | null;
  primary_exchange?: string | null;
  currency?: string | null;
  local_symbol?: string | null;
  trading_class?: string | null;
  last_trade_date_or_contract_month?: string | null;
  multiplier?: string | null;
  long_name?: string | null;
  market_name?: string | null;
  contract_month?: string | null;
  valid_exchanges?: string | null;
}

export interface ContractLookupResponse {
  success: boolean;
  data: { contracts: ManualOrderContract[] };
}

export interface ManualOrderMutationResponse {
  success: boolean;
  order_id?: number | string | null;
  message?: string | null;
}

export interface OrderPlacementParams {
  symbol: string;
  action: 'BUY' | 'SELL';
  quantity: number;
  order_type: string;
  strategy_name?: 'MANUAL' | 'NQMM';
  price?: number;
  trailing_amount?: number;
  trailing_percent?: number;
  sec_type?: string;
  tif?: 'DAY' | 'GTC';
  outside_rth?: boolean;
  con_id?: number;
  exchange?: string;
  currency?: string;
  last_trade_date_or_contract_month?: string;
  local_symbol?: string;
  trading_class?: string;
  multiplier?: string;
}

export interface ModifyOrderParams {
  action: 'BUY' | 'SELL';
  quantity: number;
  order_type: string;
  price?: number;
  trailing_amount?: number;
  trailing_percent?: number;
  tif?: 'DAY' | 'GTC';
  outside_rth?: boolean;
}

export interface OrderLookupParams {
  symbol: string;
  sec_type?: string;
  exchange?: string;
}

/** Order retrieval, placement, modification, and cancellation endpoints. */
export class OrdersApiService {
  private readonly baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private request<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    return apiRequest<T>(endpoint, options);
  }

  getOpenOrders(page = 1, pageSize = 5, secType?: string): Promise<OrdersResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());
    if (secType && secType !== 'ALL') params.append('sec_type', secType);
    return this.request<OrdersResponse>(
      `${this.baseUrl}/account/orders?${params.toString()}`,
      { timeoutMs: 6000, retries: 1 },
    );
  }

  getFilledOrdersToday(page = 1, pageSize = 5, secType?: string): Promise<OrdersResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());
    if (secType && secType !== 'ALL') params.append('sec_type', secType);
    return this.request<OrdersResponse>(
      `${this.baseUrl}/account/orders/filled?${params.toString()}`,
      { timeoutMs: 6000, retries: 1 },
    );
  }

  cancelOrder(orderId: number): Promise<APIResponse<OrderCancelData>> {
    return this.request<APIResponse<OrderCancelData>>(`${this.baseUrl}/account/orders/${orderId}/cancel`, {
      method: 'POST',
    });
  }

  modifyOrder(orderId: number, order: ModifyOrderParams): Promise<ManualOrderMutationResponse> {
    return this.request<ManualOrderMutationResponse>(`${this.baseUrl}/orders/${orderId}/modify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(order),
    });
  }

  async placeOrder(order: OrderPlacementParams): Promise<ManualOrderMutationResponse> {
    try {
      return await this.request<ManualOrderMutationResponse>(`${this.baseUrl}/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(order),
      });
    } catch (err: unknown) {
      // Re-throw with just the message to prevent stack trace issues.
      throw new Error(err instanceof Error ? err.message : 'Order placement failed');
    }
  }

  lookupOrderContracts(params: OrderLookupParams): Promise<ContractLookupResponse> {
    const searchParams = new URLSearchParams();
    searchParams.set('symbol', params.symbol);
    searchParams.set('sec_type', params.sec_type || 'FUT');
    if (params.exchange) searchParams.set('exchange', params.exchange);
    return this.request<ContractLookupResponse>(`${this.baseUrl}/orders/lookup?${searchParams.toString()}`, {
      timeoutMs: 15000,
      retries: 0,
    });
  }
}
